/**
 * "How it's made": the as-designed tree, folded so it reads the way people say
 * it, and netted against the shelf so it says what is missing.
 *
 * `designTree` in `structure.ts` answers the structural question exactly — one
 * node per BOM line, which is what a scan walks. It is the wrong shape for a
 * person reading it: an arm with seven joints is seven identical cards side by
 * side, and "a robot is two arms, each arm seven actuators" has to be counted
 * off the screen. So this does two things on top of it, both pure:
 *
 *  1. **Folds identical siblings** into one node with a count — `joint_1` …
 *     `joint_7` of the same type and revision become "Actuator × 7", with the
 *     seven position names kept. Same type *and* same revision *and* same leaf
 *     reason is what makes two subtrees identical (the explosion depends on
 *     nothing else), so the fold loses no information.
 *  2. **Nets each node against the shelf** — the structural half of §4.6's
 *     `explode()`. Building one robot with one arm already on the shelf means
 *     building one arm, which means one arm's worth of actuators, not two. The
 *     gross figure (`perRoot`) is kept beside the net one so a screen can say
 *     both. Lead times and stock on order are not here; that needs purchasing
 *     data and belongs to the planner.
 *
 * The shelf is shared across the whole tree and drawn down depth-first, parents
 * before children, positions before consumed lines — the order `designTree`
 * already emits. A type that appears in two branches (the same fastener in the
 * arm and the base) is therefore never counted twice, and which branch gets it
 * first is stable between two loads of the same data.
 *
 * The root is never netted: the question is "what does it take to build one
 * more", and a finished robot on the shelf does not answer it.
 */

import type { DesignLeaf, DesignNode } from './structure'
import type { ComponentKind, Tracking } from './types'

export interface StructureNode {
  /** The key of the first `DesignNode` folded into this one — stable across loads. */
  readonly key: string
  readonly typeId: string
  readonly typeName: string
  readonly kind: ComponentKind
  readonly tracking: Tracking
  readonly revisionId: string | null
  readonly revisionName: string | null
  readonly leaf: DesignLeaf | null
  /** The slots this node fills in its parent, in BOM order. Empty at the root
   *  and on a consumed line, which has no position. */
  readonly positions: readonly string[]
  /** How many one parent needs: the folded lines' quantities, summed. */
  readonly perParent: number
  /** How many building `quantity` roots needs, before anything is netted. */
  readonly perRoot: number
  readonly depth: number

  /** What the whole shelf holds of this type — the same figure on every node of it. */
  readonly onShelf: number
  /** How many still have to be found here once the ancestors' shelf stock is used. */
  readonly need: number
  /** Taken from the shelf for this node. */
  readonly fromShelf: number
  /**
   * `need − fromShelf`. On an assembly it is how many to build, and its
   * children's need follows from it. On a leaf it is a shortage — there is
   * nothing under it to build from.
   */
  readonly toMake: number
  /** `toMake` on a leaf, 0 on an assembly. */
  readonly short: number
  /** How many nodes in the subtree below this one are short. Not units:
   *  "3 actuators and 28 screws" does not add up to 31 of anything. */
  readonly shortBelow: number
  readonly children: readonly StructureNode[]
}

export interface Shortage {
  readonly typeId: string
  readonly typeName: string
  readonly kind: ComponentKind
  readonly tracking: Tracking
  readonly short: number
  readonly onShelf: number
  /** Gross, for `quantity` roots. */
  readonly perRoot: number
}

export interface StructureView {
  readonly root: StructureNode
  /** One line per type, biggest shortfall first. Empty when the shelf covers it. */
  readonly shortages: readonly Shortage[]
  /** Assemblies that have to be built along the way, per type. Root excluded. */
  readonly toBuild: readonly { readonly typeId: string; readonly typeName: string; readonly count: number }[]
}

/** The numbers `fold` produces, before the shelf is applied. */
type Folded = Omit<
  StructureNode,
  'onShelf' | 'need' | 'fromShelf' | 'toMake' | 'short' | 'shortBelow' | 'children'
> & { readonly children: readonly Folded[] }

/** Identical subtrees — see note 1 at the top. */
const sameKey = (n: DesignNode): string => `${n.typeId}|${n.revisionId ?? ''}|${n.leaf ?? ''}`

function fold(node: DesignNode, multiplier: number): Folded {
  const groups = new Map<string, DesignNode[]>()
  for (const child of node.children) {
    const k = sameKey(child)
    const group = groups.get(k)
    if (group === undefined) groups.set(k, [child])
    else group.push(child)
  }
  const children: Folded[] = []
  for (const members of groups.values()) {
    const first = members[0]
    if (first === undefined) continue
    const perParent = members.reduce((sum, m) => sum + m.quantity, 0)
    const folded = fold(first, multiplier * perParent)
    children.push({
      ...folded,
      positions: members.flatMap((m) => (m.position === null ? [] : [m.position])),
      perParent,
    })
  }
  return {
    key: node.key,
    typeId: node.typeId,
    typeName: node.typeName,
    kind: node.kind,
    tracking: node.tracking,
    revisionId: node.revisionId,
    revisionName: node.revisionName,
    leaf: node.leaf,
    positions: node.position === null ? [] : [node.position],
    perParent: node.quantity,
    perRoot: multiplier,
    depth: node.depth,
    children,
  }
}

/** Floating-point quantities (metres of cable) must not leave 1e-16 shortfalls. */
const tidy = (n: number): number => Math.round(n * 1e6) / 1e6

/**
 * Draw `need` of this node from `pool` (mutated), then recurse with what is
 * left to make. Returns the netted node.
 */
function net(node: Folded, need: number, pool: Map<string, number>, isRoot: boolean): StructureNode {
  const onShelf = pool.get(node.typeId) ?? 0
  const fromShelf = isRoot ? 0 : tidy(Math.max(0, Math.min(need, onShelf)))
  if (fromShelf > 0) pool.set(node.typeId, tidy(onShelf - fromShelf))
  const toMake = tidy(need - fromShelf)
  const children = node.children.map((c) => net(c, tidy(toMake * c.perParent), pool, false))
  const isLeaf = node.children.length === 0
  const short = isLeaf && !isRoot ? toMake : 0
  return {
    ...node,
    onShelf: 0, // replaced below with the whole-shelf figure, not what was left
    need,
    fromShelf,
    toMake,
    short,
    shortBelow: children.reduce((s, c) => s + c.shortBelow + (c.short > 0 ? 1 : 0), 0),
    children,
  }
}

function withShelf(node: StructureNode, onHand: (typeId: string) => number): StructureNode {
  return {
    ...node,
    onShelf: Math.max(0, onHand(node.typeId)),
    children: node.children.map((c) => withShelf(c, onHand)),
  }
}

function walk(node: StructureNode): StructureNode[] {
  return [node, ...node.children.flatMap(walk)]
}

/**
 * Fold, then net — for building `quantity` of the root (default 1; clamped to
 * at least 1, because zero robots is a question nobody asks of this screen).
 *
 * `onHand` is what is free on the shelf per type; negative readings (a ledger
 * somebody over-drew) count as none rather than as a debt to repay.
 */
export function structureView(
  design: DesignNode,
  onHand: (typeId: string) => number,
  quantity = 1,
): StructureView {
  const n = Math.max(1, Math.trunc(quantity))
  const pool = new Map<string, number>()
  const folded = fold(design, n)
  for (const node of walkFolded(folded)) {
    if (!pool.has(node.typeId)) pool.set(node.typeId, Math.max(0, onHand(node.typeId)))
  }
  const root = withShelf(net(folded, n, pool, true), onHand)

  const shortages = new Map<string, Shortage>()
  const toBuild = new Map<string, { typeId: string; typeName: string; count: number }>()
  for (const node of walk(root)) {
    if (node.short > 0) {
      const seen = shortages.get(node.typeId)
      shortages.set(node.typeId, {
        typeId: node.typeId,
        typeName: node.typeName,
        kind: node.kind,
        tracking: node.tracking,
        short: tidy((seen?.short ?? 0) + node.short),
        onShelf: node.onShelf,
        perRoot: tidy((seen?.perRoot ?? 0) + node.perRoot),
      })
    }
    if (node.depth > 0 && node.children.length > 0 && node.toMake > 0) {
      const seen = toBuild.get(node.typeId)
      toBuild.set(node.typeId, {
        typeId: node.typeId,
        typeName: node.typeName,
        count: tidy((seen?.count ?? 0) + node.toMake),
      })
    }
  }

  return {
    root,
    shortages: [...shortages.values()].sort(
      (a, b) => b.short - a.short || (a.typeName < b.typeName ? -1 : a.typeName > b.typeName ? 1 : 0),
    ),
    toBuild: [...toBuild.values()],
  }
}

function walkFolded(node: Folded): Folded[] {
  return [node, ...node.children.flatMap(walkFolded)]
}

/**
 * How many of the root the shelf could build right now, up to `cap`.
 *
 * §4.6's other question for the same explosion — "how many robots can I build
 * today: quantity 1 repeatedly until a shortage". Each round takes its parts
 * off a copy of the shelf, so the second robot cannot reuse the first one's
 * arm. A tree with nothing to source (every leaf a gap-free zero) would loop
 * forever without the cap; the caller shows "cap or more".
 */
export function buildableFromShelf(
  design: DesignNode,
  onHand: (typeId: string) => number,
  cap = 99,
): number {
  const pool = new Map<string, number>()
  const folded = fold(design, 1)
  for (const node of walkFolded(folded)) pool.set(node.typeId, Math.max(0, onHand(node.typeId)))
  for (let built = 0; built < cap; built++) {
    const trial = new Map(pool)
    const root = net(folded, 1, trial, true)
    if (walk(root).some((n) => n.short > 0)) return built
    for (const [k, v] of trial) pool.set(k, v)
  }
  return cap
}

/** Depth-first, parents first — the order an outline lists and a keyboard walks. */
export function flattenStructure(node: StructureNode): readonly StructureNode[] {
  return walk(node)
}
